import type { Content, ContentHref } from '@generalTypes/apiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import {
  selectContentItem,
  selectBaselineContentItem,
} from '@newStore/documentApi/documentApiSelectors';
import { stringDiffArrowFunction } from '@newStore/documentUI/transformProposal/asideDiffText';
import { createTypedSelector } from '@newStore/genericHelpers';
import type {
  AsideChangeMessageSelector,
  DropDownOptionsType,
  EditAsideAttachmentGroupType,
  EditComponent,
} from '@nodeTypeConfig/configTypes';
import { selectCurrentEditingNode } from '@store/selectors/asideSelectors';
import { get } from 'lodash';

export const getSelectedOptionForContent = (
  content: Content | undefined,
  options: Array<DropDownOptionsType>,
): DropDownOptionsType => {
  const importance = get(content, 'importance', 'MEDIUM');

  return options.find((ct: DropDownOptionsType) => ct.key === importance) || options[0];
};

export const selectCurrentAttachmentGroupType = createTypedSelector(
  [
    (state: RootState) => selectCurrentEditingNode(state),
    (state: RootState, config: EditAsideAttachmentGroupType) => config.options,
  ],
  (currentEditNode, curriculumTypes): DropDownOptionsType => {
    return getSelectedOptionForContent(currentEditNode, curriculumTypes);
  },
);

export const selectChangeMessageForAttachmentGroupType: AsideChangeMessageSelector<EditAsideAttachmentGroupType> =
  createTypedSelector(
    [
      (state, href: ContentHref) => selectContentItem(state, href),
      (state, href: ContentHref) => selectBaselineContentItem(state, href),
      (state, href: ContentHref, config: EditComponent) => config as EditAsideAttachmentGroupType,
    ],
    (content, originalContent, config): string | null => {
      const newValue = getSelectedOptionForContent(content, get(config, 'options'))?.label;
      const oldValue = getSelectedOptionForContent(originalContent, get(config, 'options'))?.label;

      return stringDiffArrowFunction(newValue, oldValue);
    },
  );
